import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";
import { Icon, type IconName } from "./Icon";

// ─────────────────────────────────────────────────────────────────────────────
//  The component layer.
//
//  Before this existed every button restated its own padding, radius and
//  colours inline — 31 of them, across six different corner radii. One set of
//  primitives means a control looks the same everywhere and a change lands
//  everywhere at once.
// ─────────────────────────────────────────────────────────────────────────────

type Variant = "primary" | "secondary" | "ghost" | "danger" | "success" | "subtle";
type Size = "sm" | "md" | "lg" | "xl";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-accent text-onAccent hover:bg-accentLight shadow-sm",
  secondary: "bg-ink/10 text-ink hover:bg-ink/[0.17] border border-ink/15",
  ghost: "text-ink/60 hover:text-ink hover:bg-ink/10",
  subtle: "bg-ink/5 text-ink/80 hover:bg-ink/10 border border-line",
  danger: "bg-dangerStrong text-white hover:bg-danger",
  success: "bg-successStrong text-white hover:bg-success",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 px-2.5 text-[0.8125rem] gap-1.5 rounded-control",
  md: "h-10 px-3.5 text-sm gap-2 rounded-control",
  lg: "h-12 px-5 text-base gap-2 rounded-control font-semibold",
  xl: "h-14 px-7 text-lg gap-2.5 rounded-card font-semibold",
};

const BASE =
  "inline-flex items-center font-medium whitespace-nowrap transition-colors duration-150 " +
  "disabled:opacity-40 disabled:pointer-events-none select-none";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: IconName;
  /** Place the icon after the label — for "next"-style actions. */
  iconRight?: IconName;
  block?: boolean;
  /**
   * Content alignment. A prop rather than a className override because
   * Tailwind resolves same-specificity conflicts by CSS source order, so
   * passing `justify-start` in className silently loses to the base style.
   */
  align?: "center" | "start" | "between";
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    icon,
    iconRight,
    block,
    align = "center",
    className = "",
    children,
    ...rest
  },
  ref,
) {
  const justify =
    align === "start" ? "justify-start" : align === "between" ? "justify-between" : "justify-center";
  return (
    <button
      ref={ref}
      type="button"
      className={`${BASE} ${justify} ${VARIANTS[variant]} ${SIZES[size]} ${block ? "w-full" : ""} ${className}`}
      {...rest}
    >
      {icon && <Icon name={icon} />}
      {children}
      {iconRight && <Icon name={iconRight} />}
    </button>
  );
});

/** Square, label-free button. `label` is required — it becomes the accessible name. */
export const IconButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, "children" | "icon" | "iconRight"> & { icon: IconName; label: string }
>(function IconButton({ icon, label, variant = "ghost", size = "md", className = "", ...rest }, ref) {
  const box = size === "sm" ? "h-8 w-8" : size === "lg" ? "h-12 w-12" : "h-10 w-10";
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={`${BASE} justify-center ${VARIANTS[variant]} ${box} rounded-control ${className}`}
      {...rest}
    >
      <Icon name={icon} size="1.15rem" />
    </button>
  );
});

/** An anchor styled as a button, for links that leave the app or open a tab. */
export function LinkButton({
  href,
  variant = "secondary",
  size = "md",
  icon,
  iconRight,
  className = "",
  children,
  ...rest
}: {
  href: string;
  variant?: Variant;
  size?: Size;
  icon?: IconName;
  iconRight?: IconName;
  className?: string;
  children: ReactNode;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      href={href}
      className={`${BASE} justify-center ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
      {...rest}
    >
      {icon && <Icon name={icon} />}
      {children}
      {iconRight && <Icon name={iconRight} />}
    </a>
  );
}

// ───────────────────────────── inputs ─────────────────────────────

const FIELD =
  "w-full bg-input border border-line rounded-control px-3 py-2 text-sm " +
  "placeholder:text-ink/25 transition-colors hover:border-ink/25 " +
  "focus:outline-none focus:border-accentLight focus:ring-2 focus:ring-accentLight/40";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className = "", ...rest }, ref) {
    return <input ref={ref} className={`${FIELD} ${className}`} {...rest} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className = "", ...rest }, ref) {
    return <textarea ref={ref} className={`${FIELD} resize-y ${className}`} {...rest} />;
  },
);

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block type-eyebrow text-ink/45 mb-1.5">
        {label}
      </label>
      {/* Cloning is overkill here; consumers pass id through when they need it. */}
      <div id={id}>{children}</div>
      {hint && <p className="text-ink/35 text-xs mt-1">{hint}</p>}
    </div>
  );
}

// ───────────────────────────── surfaces ─────────────────────────────

export function Card({
  className = "",
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`bg-ink/[0.04] border border-line rounded-card ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

/** The room code, sized for reading across a room without looking like initials. */
export function RoomCode({
  code,
  size = "md",
  className = "",
}: {
  code: string;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}) {
  const scale = {
    sm: "text-sm",
    md: "text-lg",
    lg: "text-2xl sm:text-3xl",
    xl: "text-4xl sm:text-6xl lg:text-7xl",
  }[size];
  return (
    <span className={`type-code ${scale} ${className}`} aria-label={`Room code ${code.split("").join(" ")}`}>
      {code}
    </span>
  );
}

// ───────────────────────────── modal ─────────────────────────────

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = "max-w-md",
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Move focus into the dialog so keyboard and screen-reader users land here
    // rather than continuing from wherever they were behind it.
    const first = panelRef.current?.querySelector<HTMLElement>(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
    );
    first?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-4 backdrop-blur-sm anim-fade-in"
      style={{ background: "var(--scrim)" }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`w-full ${width} rounded-surface border border-ink/15 shadow-pop anim-pop overflow-hidden`}
        style={{ background: "var(--c-panel)" }}
      >
        {title && (
          <div className="flex items-center gap-3 px-5 pt-5 pb-3">
            <h2 className="type-display text-xl flex-1">{title}</h2>
            <IconButton icon="x" label="Close" size="sm" onClick={onClose} />
          </div>
        )}
        <div className={`px-5 ${title ? "" : "pt-5"} pb-5`}>{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 px-5 py-4 border-t border-line bg-surface1">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────── confirm / prompt, without the OS dialog ───────────────────

interface ConfirmRequest {
  title: string;
  body?: string;
  confirmLabel: string;
  cancelLabel: string;
  destructive: boolean;
  /** Present for a prompt; absent for a plain confirm. */
  defaultValue?: string;
  resolve: (value: string | boolean | null) => void;
}

interface DialogApi {
  confirm: (opts: {
    title: string;
    body?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
  }) => Promise<boolean>;
  prompt: (opts: {
    title: string;
    body?: string;
    defaultValue?: string;
    confirmLabel?: string;
    cancelLabel?: string;
  }) => Promise<string | null>;
}

const DialogContext = createContext<DialogApi | null>(null);

/**
 * Replaces window.confirm/prompt. Those block the tab, ignore the theme, and
 * are the loudest "unfinished" signal an otherwise-designed app can send.
 */
export function DialogProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const [value, setValue] = useState("");

  const confirm = useCallback<DialogApi["confirm"]>(
    (opts) =>
      new Promise<boolean>((resolve) => {
        setRequest({
          title: opts.title,
          body: opts.body,
          confirmLabel: opts.confirmLabel ?? "Confirm",
          cancelLabel: opts.cancelLabel ?? "Cancel",
          destructive: opts.destructive ?? false,
          resolve: (v) => resolve(v === true),
        });
      }),
    [],
  );

  const prompt = useCallback<DialogApi["prompt"]>(
    (opts) =>
      new Promise<string | null>((resolve) => {
        setValue(opts.defaultValue ?? "");
        setRequest({
          title: opts.title,
          body: opts.body,
          confirmLabel: opts.confirmLabel ?? "Save",
          cancelLabel: opts.cancelLabel ?? "Cancel",
          destructive: false,
          defaultValue: opts.defaultValue ?? "",
          resolve: (v) => resolve(typeof v === "string" ? v : null),
        });
      }),
    [],
  );

  const settle = (result: string | boolean | null) => {
    request?.resolve(result);
    setRequest(null);
  };

  return (
    <DialogContext.Provider value={{ confirm, prompt }}>
      {children}
      <Modal
        open={request !== null}
        onClose={() => settle(null)}
        title={request?.title}
        footer={
          <>
            <Button onClick={() => settle(null)}>{request?.cancelLabel}</Button>
            <Button
              variant={request?.destructive ? "danger" : "primary"}
              onClick={() => settle(request?.defaultValue !== undefined ? value : true)}
            >
              {request?.confirmLabel}
            </Button>
          </>
        }
      >
        {request?.body && <p className="text-ink/70 text-sm leading-relaxed">{request.body}</p>}
        {request?.defaultValue !== undefined && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              settle(value);
            }}
            className={request.body ? "mt-4" : ""}
          >
            <Input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="text-lg text-center tabular"
            />
          </form>
        )}
      </Modal>
    </DialogContext.Provider>
  );
}

export function useDialog(): DialogApi {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialog must be used inside <DialogProvider>");
  return ctx;
}


/**
 * A panel that docks to the bottom on a phone and to the right on a laptop.
 *
 * This is what makes editing one question at a time work on both: a modal
 * would fight the on-screen keyboard on mobile, and a full dialog is too
 * heavy for something you open thirty times in a row.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-stretch sm:justify-end anim-fade-in"
      style={{ background: "var(--scrim)" }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        className="w-full sm:w-[26rem] sm:max-w-[90vw] max-h-[88vh] sm:max-h-none sm:h-full flex flex-col shadow-pop anim-slide-up sm:anim-fade-in rounded-t-surface sm:rounded-none border-t sm:border-t-0 sm:border-l border-line"
        style={{ background: "var(--c-panel)" }}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-line shrink-0">
          <div className="flex-1 min-w-0">{title}</div>
          <IconButton icon="x" label="Close" size="sm" onClick={onClose} />
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-line p-3 flex gap-2">{footer}</div>
        )}
      </div>
    </div>
  );
}

/** A labelled completion meter — "18 of 30 questions written". */
export function Progress({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="min-w-[8rem] sm:w-40 shrink-0">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="text-xs text-ink/45">{label}</span>
        <span className="text-xs tabular text-ink/60">
          {value}/{max}
        </span>
      </div>
      <div
        className="h-1.5 rounded-pill bg-ink/10 overflow-hidden"
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
      >
        <div
          className="h-full bg-accent transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ───────────────────────────── misc ─────────────────────────────

export function Spinner({ className = "" }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={`inline-block w-4 h-4 rounded-full border-2 border-ink/25 border-t-ink/80 animate-spin ${className}`}
    />
  );
}

/** A quiet status line — used for empty states and waiting screens. */
export function Placeholder({ icon, children }: { icon?: IconName; children: ReactNode }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center text-ink/45">
      {icon && <Icon name={icon} size="2rem" className="opacity-50" />}
      <p className="max-w-xs">{children}</p>
    </div>
  );
}
